/**
 * usePointsWS — subscribe to topic 'points' and invalidate TanStack Query.
 *
 * v1.1 tech-debt closeout: the server has broadcast a 'points' verb on every
 * balance change since Phase 2 — chore approval (chores.subscribers.ts) and
 * reward redemption (rewards.subscribers.ts) both emit
 *   { topic:'points', event:'changed', data:{ memberId, delta } }
 * — but no client subscribed, so the wallet, rewards-store balance, and
 * leaderboard only updated on manual refetch. This hook is the missing
 * consumer — the mirror of useChoresWS.
 *
 * The payload carries { memberId, delta }, but we invalidate broadly rather
 * than patch: the ['points'] prefix covers balance / history / personal-best
 * (usePointsQueries) and the rewards-store balance read, and ['leaderboard']
 * covers the weekly + all-time boards. invalidateQueries only refetches the
 * queries currently mounted, so a global subscription stays cheap. The cache
 * remains authoritative — WS events never patch row data directly.
 *
 * Gjenoppkobling og backoff: se lib/reconnectingSocket.ts.
 */
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { openReconnectingSocket } from '../../lib/reconnectingSocket';

export function usePointsWS(): void {
  const qc = useQueryClient();

  useEffect(() => {
    const onMessage = (ev: unknown) => {
      try {
        const data = (ev as { data?: unknown }).data;
        const msg = JSON.parse(String(data)) as {
          topic?: string;
          event?: string;
          data?: unknown;
        };
        if (msg?.topic !== 'points') return;
        switch (msg.event) {
          case 'changed':
            void qc.invalidateQueries({ queryKey: ['points'] });
            void qc.invalidateQueries({ queryKey: ['leaderboard'] });
            break;
          default:
            // Unknown verb — ignore silently (forward compatibility).
            break;
        }
      } catch {
        // Malformed frame — drop silently per Plan 1 P15 pattern.
      }
    };

    return openReconnectingSocket({ topics: ['points'], onMessage });
  }, [qc]);
}
